/**
 * Dietary / merchandising tags — shared components.
 *
 * Two tiny primitives used across the admin editor AND the guest-facing
 * menu:
 *
 *   <DietaryTagPicker value onChange />    (admin editor toggles)
 *   <DietaryTagIcons value size compact /> (guest read-only badges)
 *
 * The canonical vocabulary today is 4 tags — keep this list in sync with
 * the guest-menu render so both stay 1:1 in tests.
 *
 *   veg         green square outline + green dot     "Vegetarian"
 *   non_veg     red   square outline + red dot       "Non-vegetarian"
 *   vegan       green leaf icon                       "Vegan"
 *   bestseller  amber star + "BESTSELLER" label       "Bestseller"
 *
 * Tags are STRINGS not enums server-side, so a venue in a market that
 * needs "jain" / "halal" / "gluten_free" can be extended by adding a row
 * to `TAG_VOCAB` below — no backend change required.
 */
import React from 'react';
import { Leaf, Star } from 'lucide-react';

export const TAG_VOCAB = [
  {
    key: 'veg',
    label: 'Vegetarian',
    short: 'Veg',
    render: ({ size = 14 }) => (
      // Classic FSSAI green square + green dot — universally recognised in
      // Indian menus. Rendered as pure SVG so it scales cleanly and doesn't
      // rely on an icon-font at very small sizes.
      <svg width={size} height={size} viewBox="0 0 14 14" aria-label="Vegetarian" role="img">
        <rect x="0.75" y="0.75" width="12.5" height="12.5" rx="1.5" fill="none" stroke="#16a34a" strokeWidth="1.25" />
        <circle cx="7" cy="7" r="3" fill="#16a34a" />
      </svg>
    ),
  },
  {
    key: 'non_veg',
    label: 'Non-vegetarian',
    short: 'Non-veg',
    render: ({ size = 14 }) => (
      <svg width={size} height={size} viewBox="0 0 14 14" aria-label="Non-vegetarian" role="img">
        <rect x="0.75" y="0.75" width="12.5" height="12.5" rx="1.5" fill="none" stroke="#dc2626" strokeWidth="1.25" />
        <circle cx="7" cy="7" r="3" fill="#dc2626" />
      </svg>
    ),
  },
  {
    key: 'vegan',
    label: 'Vegan',
    short: 'Vegan',
    render: ({ size = 14 }) => <Leaf size={size} className="text-emerald-500" strokeWidth={2.5} aria-label="Vegan" />,
  },
  {
    key: 'bestseller',
    label: 'Bestseller',
    short: 'Bestseller',
    render: ({ size = 14 }) => <Star size={size} className="text-amber-400 fill-amber-400" aria-label="Bestseller" />,
  },
];

const _tagByKey = Object.fromEntries(TAG_VOCAB.map((t) => [t.key, t]));

/**
 * Read-only icon strip. Used in the guest menu and in the admin list-view.
 * `size` is the icon side length in px. `compact=true` hides labels and only
 * renders the icons — good for tight rows where every pixel counts.
 */
export const DietaryTagIcons = ({ value = [], size = 14, compact = true, className = '' }) => {
  if (!Array.isArray(value) || value.length === 0) return null;
  const items = value.map((k) => _tagByKey[k]).filter(Boolean);
  if (items.length === 0) return null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 flex-wrap ${className}`}
      data-testid="dietary-tag-icons"
    >
      {items.map((t) => (
        <span key={t.key} className="inline-flex items-center gap-1" title={t.label}>
          {t.render({ size })}
          {!compact && <span className="text-[10px] font-mono uppercase tracking-wider text-white/70">{t.short}</span>}
        </span>
      ))}
    </span>
  );
};

/**
 * Editor: renders one toggleable chip per tag. Multi-select. Tags are stored
 * on the parent draft as `dietary_tags: string[]`. Empty array = no tags.
 *
 * Ergonomically:
 *   - Selecting `veg` auto-deselects `non_veg` (mutually exclusive).
 *   - `bestseller` is orthogonal (can combine with anything).
 *   - `vegan` implies veg — selecting `vegan` auto-adds `veg` (a vegan dish
 *     is by definition vegetarian). Deselecting `veg` while `vegan` is on
 *     also removes `vegan` to keep the state consistent.
 */
export const DietaryTagPicker = ({ value = [], onChange, testIdPrefix = 'menu-edit' }) => {
  const has = (k) => Array.isArray(value) && value.includes(k);
  const toggle = (k) => {
    const set = new Set(value || []);
    if (set.has(k)) {
      set.delete(k);
      if (k === 'veg') set.delete('vegan');   // veg off → vegan off
    } else {
      set.add(k);
      if (k === 'veg') set.delete('non_veg'); // veg on → non_veg off
      if (k === 'non_veg') { set.delete('veg'); set.delete('vegan'); }
      if (k === 'vegan') set.add('veg');      // vegan on → veg on
    }
    onChange(Array.from(set));
  };

  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-dietary-tags`}>
      {TAG_VOCAB.map((t) => {
        const active = has(t.key);
        return (
          <button
            type="button"
            key={t.key}
            onClick={() => toggle(t.key)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-mono uppercase tracking-wider transition-colors ${
              active
                ? 'bg-white/10 border-white/40 text-white'
                : 'bg-black/30 border-white/10 text-white/50 hover:text-white/80 hover:border-white/20'
            }`}
            data-testid={`${testIdPrefix}-dietary-tag-${t.key}`}
            aria-pressed={active}
          >
            {t.render({ size: 12 })}
            {t.short}
          </button>
        );
      })}
    </div>
  );
};

export default DietaryTagIcons;
